/**
 * @fileoverview Top SKUs by Profit - horizontal bar chart using ShadCN Charts (Recharts)
 * @module shared/components/ui/TopSkusByProfitChart
 */

import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils/cn'
import { Card } from '@/shared/components/ui'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'
import { useNavigate } from 'react-router-dom'
import { ChartCardHeader } from './ChartCardHeader'
import { Package } from 'lucide-react'
import { ChartEmptyState } from './ChartEmptyState'
import { ChartProfitTooltip, type ChartTooltipMetric } from './ChartProfitTooltip'
import { ChartConfig, ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { getThemeColors } from '@/components/ui/chart-utils'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { useTopSkusByProfitData } from '@/shared/hooks/dashboard'
import { useThemeSignal } from '@/shared/hooks/useThemeSignal'
import { Thumbnail } from '@/shared/components/ui'
import { useProductGroupsMetadata } from '@/shared/hooks'

interface TopSkusByProfitChartProps {
  className?: string
}

interface ChartDataItem {
  name: string
  profit: number
  revenue: number
  adSpend: number
  cogs: number
  units: number
  sku: string
  imageUrl: string | null
}

const ICON_SIZE = 40
const ROW_HEIGHT = 52 // Icon size + padding for each row

export function TopSkusByProfitChart({ className }: TopSkusByProfitChartProps): ReactElement {
  const { t, i18n } = useTranslation('features-home')
  const { data, isLoading, error } = useTopSkusByProfitData(10)
  const compactMoney = useCompactMoney()
  const navigate = useNavigate()
  useThemeSignal()
  const { getGroup } = useProductGroupsMetadata()

  const navigateToProduct = (item: ChartDataItem): void => {
    const group = getGroup(item.sku)
    navigate(`/dashboard/product/${encodeURIComponent(item.sku)}`, {
      state: {
        memberSkus: group?.members ?? [],
        productName: item.name,
        imageUrl: item.imageUrl,
      },
    })
  }

  const colors = getThemeColors()

  const chartData: ChartDataItem[] = useMemo(() => {
    return data.map(item => {
      const group = getGroup(item.id)
      return {
        name: item.name,
        profit: item.profit,
        revenue: item.revenue,
        adSpend: item.adSpend,
        cogs: item.cogs,
        units: item.units,
        sku: item.id,
        imageUrl: group?.imageUrl ?? null,
      }
    })
  }, [data, getGroup])

  const chartConfig = useMemo(
    () =>
      ({
        profit: {
          label: t('charts.topSkusByProfit.profit'),
          color: colors.positive,
        },
      }) satisfies ChartConfig,
    [colors.positive, t]
  )

  if (isLoading) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.topSkusByProfit.title')}
          href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=products"
        />
        <div className="px-6 pt-4 pb-8">
          <div className="flex h-chart-lg items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-foreground/20 border-t-foreground" />
          </div>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.topSkusByProfit.title')}
          href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=products"
        />
        <div className="px-6 pt-4 pb-8">
          <div className="flex h-chart-lg items-center justify-center text-[--color-negative]">
            {error}
          </div>
        </div>
      </Card>
    )
  }

  if (!data.length) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.topSkusByProfit.title')}
          href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=products"
        />
        <div className="px-6 pt-4 pb-8">
          <ChartEmptyState
            variant="bars"
            icon={Package}
            message={t('charts.topSkusByProfit.noDataForPeriod')}
            hint={t('charts.emptyHint')}
            className="h-chart-lg"
          />
        </div>
      </Card>
    )
  }

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30', className)}>
      {/* Header section */}
      <ChartCardHeader
        title={t('charts.topSkusByProfit.title')}
        href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=products"
        className="rounded-t-xl"
      />

      {/* Chart section with border separator */}
      <div className="px-6 pt-4 pb-8 flex" style={{ height: `${chartData.length * ROW_HEIGHT + 48}px` }}>
        {/* Product icons column - CSS Grid matches Recharts bar distribution */}
        <div
          className="grid pr-3"
          style={{ gridTemplateRows: `repeat(${chartData.length}, 1fr)` }}
        >
          {chartData.map(item => (
            <div
              key={item.sku}
              className="flex items-center cursor-pointer"
              onClick={() => navigateToProduct(item)}
            >
              <Thumbnail
                src={item.imageUrl}
                alt={item.name}
                size={ICON_SIZE}
                className="rounded-md ring-1 ring-border/50"
              />
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <ChartContainer config={chartConfig} className="flex-1">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{
              left: 0,
              right: 80,
              top: 0,
              bottom: 0,
            }}
            className="cursor-pointer"
            onClick={(state: Record<string, unknown>) => {
              const idx = state?.activeTooltipIndex as number | undefined
              if (idx != null && chartData[idx]) navigateToProduct(chartData[idx])
            }}
          >
            <defs>
              {/* Lengthwise fade (dim base, full color at the tip) — horizontal
                  counterpart of the donuts' top-lit slice gradients. */}
              <linearGradient id="top-skus-bar-fill" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--color-profit)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--color-profit)" stopOpacity={0.95} />
              </linearGradient>
            </defs>
            <YAxis dataKey="name" type="category" hide />
            <XAxis dataKey="profit" type="number" hide />
            <ChartTooltip
              cursor={{ fill: 'hsl(var(--muted) / 0.2)' }}
              wrapperStyle={{ zIndex: 50 }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const item = payload[0]?.payload as ChartDataItem
                if (!item) return null

                const roas = item.adSpend > 0 ? item.revenue / item.adSpend : null
                const metrics: ChartTooltipMetric[] = [
                  { label: t('charts.tooltipMetrics.revenue'), value: compactMoney(item.revenue) },
                  { label: t('charts.tooltipMetrics.adSpend'), value: compactMoney(item.adSpend) },
                  {
                    label: t('charts.tooltipMetrics.roas'),
                    value: roas != null ? `${roas.toFixed(1)}×` : '—',
                  },
                  { label: t('charts.tooltipMetrics.cogs'), value: compactMoney(item.cogs) },
                ]

                return (
                  <ChartProfitTooltip
                    lead={
                      <Thumbnail
                        src={item.imageUrl}
                        alt={item.name}
                        size={32}
                        className="rounded-sm"
                      />
                    }
                    name={item.name}
                    profit={item.profit}
                    profitLabel={t('charts.topSkusByProfit.profit')}
                    formatMoney={compactMoney}
                    metrics={metrics}
                    units={Math.round(item.units).toLocaleString(i18n.language)}
                  />
                )
              }}
            />
            <Bar
              dataKey="profit"
              fill="url(#top-skus-bar-fill)"
              radius={9}
              barSize={18}
              background={{ fill: 'var(--color-muted)', fillOpacity: 0.25, radius: 9 }}
              activeBar={{ fill: 'var(--color-profit)' }}
            >
              <LabelList
                dataKey="profit"
                position="right"
                offset={8}
                className="fill-foreground font-mono font-medium"
                fontSize={14}
                formatter={(value) => (typeof value === 'number' ? compactMoney(value).replace(/ /g, '\u00a0') : String(value))}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>
    </Card>
  )
}
